import { useState, useEffect } from 'react';
import {
    Box,
    Container,
    Typography,
    CircularProgress,
    Paper,
    ThemeProvider,
    createTheme,
    alpha,
    Stack,
    useTheme,
} from '@mui/material';
import { useApiClient } from '../../ApiClientProvider';
import { BrandingService, CompanyBranding } from '../../services/brandingService';

/**
 * Extract subdomain from current hostname
 * Supports:
 * - Production: acmecorp.attendin.io → 'acmecorp'
 * - Dev: acmecorp.dev.attendin.io → 'acmecorp'
 * - QA: acmecorp.qa.attendin.io → 'acmecorp'
 * - Local testing: events.localhost → 'events'
 */
function getSubdomain(): string | null {
    const hostname = window.location.hostname;

    // localhost (no subdomain) or direct attendin.io - no subdomain
    if (hostname === 'localhost' || hostname === 'attendin.io' || hostname === 'www.attendin.io') {
        return null;
    }

    const parts = hostname.split('.');

    // events.localhost - local testing subdomain
    if (parts.length === 2 && parts[1] === 'localhost') {
        return parts[0]; // Extract "events" from events.localhost
    }

    // dev.attendin.io or qa.attendin.io - environment URLs without company subdomain
    if (parts.length === 3 && (parts[0] === 'dev' || parts[0] === 'qa')) {
        return null;
    }

    // acmecorp.dev.attendin.io or acmecorp.qa.attendin.io - company subdomain in environment
    if (parts.length === 4 && (parts[1] === 'dev' || parts[1] === 'qa')) {
        return parts[0]; // Extract "acmecorp" from acmecorp.dev.attendin.io
    }

    // acmecorp.attendin.io - company subdomain in production
    if (parts.length === 3) {
        return parts[0]; // Extract "acmecorp" from acmecorp.attendin.io
    }

    return null;
}

/**
 * Get the appropriate root domain based on current environment
 * - events.localhost → localhost
 * - acmecorp.dev.attendin.io → dev.attendin.io
 * - acmecorp.qa.attendin.io → qa.attendin.io
 * - acmecorp.attendin.io → attendin.io
 */
function getRootDomain(): string {
    const hostname = window.location.hostname;
    const parts = hostname.split('.');

    // Local testing
    if (parts.includes('localhost')) {
        return 'localhost';
    }

    // Check for environment in hostname
    if (parts.includes('dev')) {
        return 'dev.attendin.io';
    }
    if (parts.includes('qa')) {
        return 'qa.attendin.io';
    }

    // Default to production
    return 'attendin.io';
}

export default function BrandedPublicPage() {
    const theme = useTheme();

    const apiClient = useApiClient('managementClient');
    const brandingService = new BrandingService(apiClient);

    const [loading, setLoading] = useState(true);
    const [branding, setBranding] = useState<CompanyBranding | null>(null);
    const [logoUrl, setLogoUrl] = useState<string | null>(null);
    const [error, setError] = useState<string | null>(null);

    const subdomain = getSubdomain();

    useEffect(() => {
        const fetchBranding = async () => {
            if (!subdomain) {
                setError('No subdomain detected');
                setLoading(false);
                return;
            }

            try {
                setLoading(true);
                const brandingData = await brandingService.getCompanyBrandingBySubdomain(subdomain);
                setBranding(brandingData);

                // Fetch logo SAS URL if company has a logo
                if (brandingData.logoUrl) {
                    const logo = await brandingService.getCompanyLogoBySubdomain(subdomain);
                    setLogoUrl(logo);
                }
            } catch (err: any) {
                // If subdomain doesn't exist (404), redirect to root domain
                if (err.response?.status === 404) {
                    const rootDomain = getRootDomain();
                    window.location.href = `${window.location.protocol}//${rootDomain}`;
                    return;
                }
                setError(err.response?.data || 'Failed to load company branding');
            } finally {
                setLoading(false);
            }
        };

        fetchBranding();
    }, [subdomain]);

    // Create dynamic theme based on company colors
    const brandedTheme = branding
        ? createTheme({
            palette: {
                mode: (branding.mode as 'light' | 'dark') || 'light',
                primary: {
                    main: branding.primaryColour || theme.palette.primary.main,
                },
                secondary: {
                    main: branding.secondaryColour || theme.palette.secondary.main,
                },
            },
        })
        : createTheme();

    if (loading) {
        return (
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    minHeight: '100vh',
                }}
            >
                <CircularProgress />
            </Box>
        );
    }

    if (error || !branding) {
        return (
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    minHeight: '100vh',
                }}
            >
                <Typography variant="h6" color="error">
                    {error || 'Company not found'}
                </Typography>
            </Box>
        );
    }

    return (
        <ThemeProvider theme={brandedTheme}>
            <Box
                sx={{
                    minHeight: '100vh',
                    bgcolor: 'background.default',
                }}
            >
                {/* Modern 2026 Branded Header */}
                <Box
                    sx={{
                        background: `linear-gradient(135deg, ${branding.primaryColour || theme.palette.primary.main} 0%, ${branding.secondaryColour || theme.palette.secondary.main} 100%)`,
                        backdropFilter: 'blur(10px)',
                        position: 'relative',
                        overflow: 'hidden',
                        '&::before': {
                            content: '""',
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            background: 'radial-gradient(circle at 20% 50%, rgba(255,255,255,0.1) 0%, transparent 50%)',
                            pointerEvents: 'none',
                        },
                    }}
                >
                    <Container maxWidth="lg">
                        <Box
                            sx={{
                                py: { xs: 6, md: 8 },
                                display: 'flex',
                                flexDirection: { xs: 'column', md: 'row' },
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                gap: { xs: 3, md: 4 },
                                position: 'relative',
                                zIndex: 1,
                            }}
                        >
                            {/* Logo and Company Name */}
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 3,
                                    flex: 1,
                                }}
                            >
                                {branding.logoUrl && logoUrl && (
                                    <Box
                                        component="img"
                                        src={logoUrl}
                                        alt={`${branding.name} logo`}
                                        onError={(e) => {
                                            // Hide logo if it fails to load
                                            (e.target as HTMLImageElement).style.display = 'none';
                                        }}
                                        sx={{
                                            height: { xs: 60, md: 80 },
                                            width: 'auto',
                                            maxWidth: 200,
                                            objectFit: 'contain',
                                            filter: 'drop-shadow(0 4px 12px rgba(0,0,0,0.15))',
                                        }}
                                    />
                                )}
                                <Box>
                                    <Typography
                                        variant="h2"
                                        sx={{
                                            fontWeight: 800,
                                            color: 'white',
                                            lineHeight: 1.2,
                                            fontSize: { xs: '2rem', md: '3rem' },
                                            letterspacing: '-0.5px',
                                            textShadow: '0 2px 8px rgba(0,0,0,0.1)',
                                        }}
                                    >
                                        {branding.name}
                                    </Typography>
                                    <Box
                                        sx={{
                                            height: 4,
                                            width: { xs: 40, md: 60 },
                                            background: 'rgba(255,255,255,0.6)',
                                            borderRadius: '2px',
                                            mt: 1.5,
                                        }}
                                    />
                                </Box>
                            </Box>

                            {/* Accent Elements */}
                            <Box
                                sx={{
                                    display: { xs: 'none', md: 'flex' },
                                    alignItems: 'center',
                                    gap: 2,
                                }}
                            >
                                <Box
                                    sx={{
                                        width: 80,
                                        height: 80,
                                        borderRadius: '16px',
                                        background: 'rgba(255,255,255,0.1)',
                                        backdropFilter: 'blur(10px)',
                                        border: '1px solid rgba(255,255,255,0.2)',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                    }}
                                >
                                    <Typography
                                        sx={{
                                            fontSize: '2.5rem',
                                            color: 'rgba(255,255,255,0.8)',
                                        }}
                                    >
                                        ✨
                                    </Typography>
                                </Box>
                            </Box>
                        </Box>
                    </Container>
                </Box>

                {/* Main Content */}
                <Container maxWidth="lg">
                    <Box sx={{ py: 6 }}>
                        {/* Events Section */}
                        <Paper
                            sx={{
                                p: { xs: 3, md: 4 },
                                background: 'rgba(255, 255, 255, 0.8)',
                                backdropFilter: 'blur(10px)',
                                border: '1px solid',
                                borderColor: 'divider',
                                borderRadius: '12px',
                            }}
                        >
                            <Typography
                                variant="h4"
                                gutterBottom
                                sx={{
                                    fontWeight: 700,
                                    mb: 3,
                                    color: 'primary.main',
                                }}
                            >
                                Upcoming Events
                            </Typography>
                            <Typography variant="body1" color="text.secondary">
                                Events will be displayed here...
                            </Typography>
                            {/* TODO: Add public events list */}
                        </Paper>
                    </Box>
                </Container>
            </Box>
        </ThemeProvider>
    );
}
